import { useEffect, useMemo } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft, Award, CheckCircle2, BadgeCheck } from "lucide-react";
import { printPolicyPage } from "@/lib/printPolicy";
import { useAgencyConfig, findIntermediario } from "@/hooks/useAgencyConfig";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";

/**
 * Pagina Chi Siamo / Team — conforme Reg. IVASS n. 40/2018 art. 56 (possibilità
 * per il cliente di verificare le qualifiche degli intermediari iscritti al RUI).
 *
 * FIX 4 (v1.54.0.0): i RUI individuali sono letti dinamicamente da
 * `/api/public/agency-config` (single source of truth). Le biografie e
 * qualifiche restano statiche perché non fanno parte della config identificativa.
 */
const TEAM_STATIC = [
  {
    codice: "christian_colucci",
    nome: "Christian Colucci",
    ruolo: "Socio Amministratore — Intermediario Assicurativo",
    qualifiche: ["Intermediario RUI Sez. A", "Consulente tecnico rischio", "Specializzazione professioni sanitarie"],
    bio: "Gestisce direttamente le pratiche complesse e la consulenza per professionisti sanitari, strutture e aziende. Oltre 20 anni nel settore assicurativo campano.",
  },
  {
    codice: "ciro_colucci",
    nome: "Ciro Colucci",
    ruolo: "Socio Amministratore — Intermediario Assicurativo",
    qualifiche: ["Intermediario RUI Sez. A", "Specializzazione rami danni e noleggio"],
    bio: "Responsabile del rapporto con le compagnie e della gestione del portafoglio retail. Esperto in prodotti auto e famiglia.",
  },
];

function _fmtDateIt(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return (d && m && y) ? `${d}/${m}/${y}` : iso;
}

function handleDownload() {
  printPolicyPage({
    title: "Chi Siamo - Colucci Assicuratori",
    contentElementId: "chisiamo-content",
    headerHtml: `<h1>CHRISTIAN E CIRO COLUCCI S.A.S.</h1><p>P.IVA 07618011212 &mdash; RUI A000464752</p>`,
  });
}

export default function ChiSiamoPage() {
  const [params] = useSearchParams();
  const { data: cfg } = useAgencyConfig();
  const ruiSocieta = cfg?.societa?.rui || "A000464752";
  const team = useMemo(() => TEAM_STATIC.map((p) => {
    const i = findIntermediario(cfg, p.codice);
    return {
      ...p,
      rui: `${ruiSocieta} (società)`,
      rui_individuale: i?.rui || "",
      rui_individuale_data: _fmtDateIt(i?.data_iscrizione),
    };
  }), [cfg, ruiSocieta]);
  useEffect(() => {
    if (params.get("download") === "1") {
      const t = setTimeout(() => handleDownload(), 600);
      return () => clearTimeout(t);
    }
  }, [params]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="chisiamo-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-12">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Torna alla Home
            </Link>
            <Button onClick={handleDownload} variant="outline" className="rounded-xl border-[#E5E2DB] text-sm gap-2">
              <Download className="w-4 h-4" /> Scarica PDF
            </Button>
          </div>

          <div id="chisiamo-content">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-xl bg-[#163321]/10 flex items-center justify-center">
                <Award className="w-5 h-5 text-[#163321]" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-medium text-[#163321]">Chi Siamo</h1>
                <p className="text-xs text-[#4A5E51] mt-0.5">Il team Colucci Assicuratori — qualifiche e iscrizioni RUI</p>
              </div>
            </div>

            <section className="bg-white rounded-2xl border border-[#E5E2DB] p-6 mb-6">
              <h2 className="text-lg font-heading text-[#163321] mb-3">La società</h2>
              <p className="text-sm text-[#4A5E51] leading-relaxed mb-3">
                <strong>CHRISTIAN E CIRO COLUCCI S.A.S.</strong> è un'agenzia di intermediazione assicurativa operante in Campania dal 1982.
                Opera in conformità al D.Lgs. 209/2005 (Codice delle Assicurazioni Private) e ai Regolamenti IVASS.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="bg-[#F5F4F0] rounded-lg p-3">
                  <p className="text-[11px] text-[#4A5E51]/70 uppercase tracking-wider">Forma giuridica</p>
                  <p className="text-[#163321] font-semibold">Società in accomandita semplice</p>
                </div>
                <div className="bg-[#F5F4F0] rounded-lg p-3">
                  <p className="text-[11px] text-[#4A5E51]/70 uppercase tracking-wider">P.IVA / C.F.</p>
                  <p className="text-[#163321] font-semibold">07618011212</p>
                </div>
                <div className="bg-[#F5F4F0] rounded-lg p-3">
                  <p className="text-[11px] text-[#4A5E51]/70 uppercase tracking-wider">Iscrizione RUI</p>
                  <p className="text-[#163321] font-semibold">A000464752 (Sez. A)</p>
                </div>
                <div className="bg-[#F5F4F0] rounded-lg p-3">
                  <p className="text-[11px] text-[#4A5E51]/70 uppercase tracking-wider">Vigilanza</p>
                  <p className="text-[#163321] font-semibold">IVASS — <a href="https://www.ivass.it" target="_blank" rel="noopener noreferrer" className="underline">ivass.it</a></p>
                </div>
              </div>
              <p className="mt-3 text-[11px] text-[#4A5E51]">
                Verifica pubblica dell'iscrizione: <a href="https://ruipubblico.ivass.it/rui-pubblica/ng/" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">ruipubblico.ivass.it/rui-pubblica/ng/</a>
              </p>
            </section>

            <h2 className="text-lg font-heading text-[#163321] mb-3 mt-8">Il team</h2>
            <div className="space-y-4">
              {team.map((p, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#E5E2DB] p-6 card">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#163321]/10 flex items-center justify-center text-[#163321] font-semibold">
                      {p.nome.split(" ").map(s => s[0]).join("")}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-[#163321] text-lg">{p.nome}</h3>
                      <p className="text-sm text-[#4A5E51]">{p.ruolo}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase bg-[#163321]/10 text-[#163321] px-2 py-0.5 rounded-md">
                      <BadgeCheck className="w-3 h-3" /> RUI {p.rui}
                    </span>
                  </div>
                  <p className="text-sm text-[#4A5E51] leading-relaxed mb-1">{p.bio}</p>
                  {p.rui_individuale && (
                    <p className="text-[11px] text-[#4A5E51] mb-3" data-testid="rui-individuale">
                      Iscritto RUI Sez. A n. <strong className="text-[#163321]">{p.rui_individuale}</strong> dal {p.rui_individuale_data}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {p.qualifiche.map((q, j) => (
                      <span key={j} className="inline-flex items-center gap-1 text-[11px] bg-[#F5F4F0] text-[#4A5E51] px-2 py-1 rounded-md">
                        <CheckCircle2 className="w-3 h-3 text-[#163321]" /> {q}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* v1.55 — Recensioni verificate Google (aggregato 3 sedi) */}
            <div className="mt-8 -mx-4 sm:-mx-6" data-testid="chisiamo-google-reviews">
              <GoogleReviewsCarousel limit={6} showHeader={true} />
            </div>

            {/* Trasparenza provvigioni */}
            <section className="bg-[#F5F4F0] rounded-2xl p-6 mt-8">
              <h2 className="text-lg font-heading text-[#163321] mb-3">Come siamo remunerati</h2>
              <p className="text-sm text-[#4A5E51] leading-relaxed mb-2">
                In conformità alla Direttiva UE 2016/97 (IDD), recepita in Italia dal D.Lgs. 68/2018, e al Regolamento IVASS n. 40/2018, Colucci Assicuratori informa il cliente circa la natura della propria remunerazione:
              </p>
              <ul className="list-disc pl-5 text-sm text-[#4A5E51] leading-relaxed space-y-1.5">
                <li>
                  <strong>Provvigioni</strong>: per l'intermediazione dei prodotti assicurativi riceviamo provvigioni direttamente dalle Imprese di Assicurazione emittenti. La natura e l'ammontare della remunerazione sono indicati negli <strong>Allegati 3 e 4 del Reg. IVASS 40/2018</strong> consegnati prima della sottoscrizione.
                </li>
                <li>
                  <strong>Consulenza a parcella</strong>: per le prestazioni di consulenza specialistica (<Link to="/consulenza-assicurativa" className="text-[#163321] underline">pagina dedicata</Link>) applichiamo un onorario trasparente indicato in fase di prenotazione, incassato tramite circuito sicuro Stripe.
                </li>
                <li>
                  <strong>Noleggio Lungo Termine</strong>: eventuali provvigioni derivanti dall'intermediazione di contratti NLT (partner Arval e Ayvens) sono indicate nel contratto di noleggio.
                </li>
              </ul>
              <p className="text-xs text-[#4A5E51]/70 mt-3">
                Il cliente ha sempre il diritto di chiedere dettagli sulla remunerazione dell'intermediario (art. <strong>120-bis</strong> D.Lgs. 209/2005 — Trasparenza sulle remunerazioni).
              </p>
            </section>

            {/* Conflitti di interesse */}
            <section className="bg-white rounded-2xl border border-[#E5E2DB] p-6 mt-4" data-testid="conflitti-interesse">
              <h2 className="text-lg font-heading text-[#163321] mb-3">Gestione dei conflitti di interesse</h2>
              <p className="text-sm text-[#4A5E51] leading-relaxed mb-3">
                Ai sensi degli articoli <strong>119-bis</strong> (regole di comportamento e conflitti di interesse) e <strong>120-ter</strong> (Trasparenza sui conflitti di interesse) del D.Lgs. 209/2005, dichiariamo:
              </p>
              <ul className="list-disc pl-5 text-sm text-[#4A5E51] leading-relaxed space-y-1.5">
                <li>
                  Non deteniamo <strong>partecipazioni dirette o indirette superiori al 10%</strong> nel capitale sociale o nei diritti di voto di alcuna impresa di assicurazione.
                </li>
                <li>
                  Nessuna impresa di assicurazione e nessuna sua controllante detiene partecipazioni superiori al 10% nel nostro capitale sociale.
                </li>
                <li>
                  La consulenza è prestata sulla base di un'analisi imparziale e personale dei prodotti disponibili sul mercato, ovvero in base ad accordi non esclusivi con le compagnie partner indicate nell'<strong>Allegato 4 Reg. IVASS 40/2018</strong> consegnato a ogni cliente prima della sottoscrizione.
                </li>
                <li>
                  In caso di potenziale conflitto di interesse, il cliente viene informato preventivamente in forma scritta e può comunque scegliere di proseguire o richiedere proposte alternative senza alcun costo.
                </li>
              </ul>
            </section>

            {/* Massimali RC Auto minimi di legge */}
            <section className="bg-white rounded-2xl border border-[#E5E2DB] p-6 mt-4" data-testid="massimali-rca">
              <h2 className="text-lg font-heading text-[#163321] mb-3">Massimali minimi RC Auto (art. 128 CAP)</h2>
              <p className="text-sm text-[#4A5E51] leading-relaxed mb-3">
                I massimali minimi per la copertura RC Auto obbligatoria sono fissati dall'art. 128 del Codice delle Assicurazioni Private (D.Lgs. 209/2005), distinti tra <strong>danni alle persone</strong> e <strong>danni alle cose</strong>, e vengono <strong>aggiornati periodicamente con Decreto Ministeriale</strong> in base alla rivalutazione ISTAT.
              </p>
              <div className="bg-[#F5F4F0] rounded-xl p-4 mb-3">
                <p className="text-xs text-[#4A5E51] leading-relaxed">
                  Per consultare i <strong>massimali minimi attualmente in vigore</strong> aggiornati all'ultimo Decreto Ministeriale, fai riferimento al sito ufficiale IVASS:{" "}
                  <a href="https://www.ivass.it/consumatori/assicurazioni-veicoli/" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline font-medium">
                    ivass.it/consumatori/assicurazioni-veicoli
                  </a>
                  {" "}— oppure alla Gazzetta Ufficiale per il D.M. di rivalutazione più recente.
                </p>
              </div>
              <p className="text-xs text-[#4A5E51]/70">
                In sede di consulenza valutiamo sempre l'<strong>adeguatezza dei massimali</strong> rispetto al patrimonio e al profilo di rischio del cliente, suggerendo eventuali integrazioni quando i minimi di legge risultano insufficienti.
              </p>
            </section>

            {/* Attestato di rischio art. 134 CAP */}
            <section className="bg-[#F5F4F0] rounded-2xl p-6 mt-4" data-testid="attestato-rischio">
              <h2 className="text-lg font-heading text-[#163321] mb-3">Attestato di rischio (art. 134 CAP)</h2>
              <p className="text-sm text-[#4A5E51] leading-relaxed mb-2">
                L'attestato di rischio è il documento elettronico che riassume la storia assicurativa del veicolo (sinistri, classe di merito CU, anni di maturazione). Ai sensi dell'art. 134 del D.Lgs. 209/2005:
              </p>
              <ul className="list-disc pl-5 text-sm text-[#4A5E51] leading-relaxed space-y-1.5">
                <li>
                  L'impresa è tenuta a consegnarlo al contraente <strong>almeno 30 giorni prima della scadenza</strong> della polizza.
                </li>
                <li>
                  Ha validità <strong>5 anni</strong> dalla data di scadenza dell'ultimo contratto stipulato.
                </li>
                <li>
                  È sempre disponibile per il download dall'<strong>area riservata cliente</strong> del nostro sito (<em>anche dal sito ufficiale della Compagnia che ha emesso il contratto</em>) e tramite la <strong>Banca Dati ATRC IVASS</strong>.
                </li>
                <li>
                  Non esistono più gli attestati cartacei: dal 1° luglio 2015 l'attestato è solo in formato elettronico e contiene la classe di merito Universale (CU) da 1ª a 18ª.
                </li>
              </ul>
            </section>

            {/* Sconti obbligatori art. 132-ter */}
            <section className="bg-white rounded-2xl border border-[#E5E2DB] p-6 mt-4" data-testid="sconti-obbligatori">
              <h2 className="text-lg font-heading text-[#163321] mb-3">Sconti obbligatori RC Auto (art. 132-ter CAP)</h2>
              <p className="text-sm text-[#4A5E51] leading-relaxed mb-3">
                La legge prevede sconti obbligatori sulla tariffa RC Auto al ricorrere di determinate condizioni. In sede di preventivo verifichiamo l'applicabilità di:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { t: "Scatola nera", d: "Sconto significativo per installazione di dispositivo elettronico (black box) di registrazione comportamento di guida (art. 132-ter c.1 lett. a)." },
                  { t: "Provincia ad alta sinistrosità", d: "Sconto per assicurati in classe CU bassa (1-3) residenti in province con tariffe medie superiori alla media nazionale." },
                  { t: "Legge Bersani (D.L. 7/2007)", d: "Eredità della classe di merito da un altro veicolo del nucleo familiare al momento della prima immatricolazione/prima assicurazione del veicolo. Funziona solo tra veicoli dello stesso tipo." },
                  { t: "RC Familiare (estensione 2020)", d: "Estensione della Bersani: permette di ereditare la classe di merito anche al rinnovo della polizza e tra veicoli di tipo diverso (auto↔moto↔autocarro). Richiede assenza di sinistri con colpa principale negli ultimi 5 anni sul veicolo donante." },
                  { t: "Guida esperta", d: "Riduzione di premio per clausola di guida esclusiva o esperta (esclusione dei neopatentati o vincolo guida solo del contraente)." },
                  { t: "Antifurto e dispositivi di sicurezza", d: "Sconto per veicoli dotati di antifurto satellitare, immobilizer o blocco motore certificato." },
                ].map((s, i) => (
                  <div key={i} className="bg-[#F5F4F0] rounded-xl p-3">
                    <p className="text-sm font-semibold text-[#163321] mb-1">{s.t}</p>
                    <p className="text-xs text-[#4A5E51] leading-relaxed">{s.d}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#4A5E51]/70 mt-3">
                I parametri di applicazione e l'entità degli sconti sono determinati da ciascuna impresa di assicurazione e indicati nelle relative <strong>Note Informative</strong> e nel <strong>DIP Aggiuntivo</strong>.
              </p>
            </section>

            {/* Formazione e aggiornamento */}
            <section className="bg-white rounded-2xl border border-[#E5E2DB] p-6 mt-4">
              <h2 className="text-lg font-heading text-[#163321] mb-3">Formazione continua</h2>
              <p className="text-sm text-[#4A5E51] leading-relaxed">
                Gli intermediari iscritti al RUI sono soggetti all'obbligo di formazione professionale continua di <strong>60 ore ogni 3 anni</strong>,
                in conformità all'art. <strong>110, comma 4</strong> del Codice delle Assicurazioni Private e all'art. 89 del <strong>Regolamento IVASS n. 40/2018</strong>. I nostri collaboratori
                aggiornano costantemente le proprie competenze in materia di distribuzione assicurativa, normativa IVASS, antiriciclaggio, GDPR e
                prodotti di investimento assicurativo.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
